import type { ChangeEvent, ComponentProps, ReactNode } from 'react';
import { CheckboxInput, FormField } from '@george-labs.com/design-system';
import type { FORM_FIELD_LABEL_POSITION } from '@george-labs.com/design-system/types/components/Form';

import { useField } from '@luna/form2';

import type { FormFieldProps } from './types/FormFieldProps';
import { shouldShowError } from './FieldErrorVisibilityService';

interface Props<FormValues> extends FormFieldProps<FormValues> {
    checkboxLabel?: ReactNode;
    inputProps?: Partial<ComponentProps<typeof CheckboxInput>>;
    isErrorVisible?: boolean;
    shouldClearOnUnmount?: boolean;
}

interface FormCheckboxFieldStatics {
    LABEL_POSITION: typeof FORM_FIELD_LABEL_POSITION;
}

const LABEL_COLUMN_WIDTH = 4;

export function FormCheckboxField<FormValues = void>({
    'data-cy': dataCy,
    fieldName,
    label,
    labelPosition,
    labelColumnWidth = LABEL_COLUMN_WIDTH,
    labelClassName,
    checkboxLabel,
    placeholder,
    warning,
    info,
    alert,
    description,
    disabled,
    required = false,
    inputProps,
    isErrorVisible = true,
    addon,
    shouldClearOnUnmount,
    showErrorBeforeBlur,
}: Props<FormValues>) {
    const [fieldProps, { touched, error, dirty }] = useField<boolean>(fieldName, {
        type: 'checkbox',
        shouldClearOnUnmount,
    });

    const isFieldErrorVisible = shouldShowError(
        isErrorVisible,
        touched,
        error,
        showErrorBeforeBlur,
        dirty,
    );

    return (
        <FormField
            input={CheckboxInput}
            inputProps={{
                ...fieldProps,
                ...inputProps,
                value: 'on',
                label: checkboxLabel,
                'data-cy': dataCy,
                onChange: (e: ChangeEvent<HTMLInputElement>) => {
                    fieldProps.onChange({
                        target: {
                            value: e.target.checked,
                        },
                    });
                    inputProps?.onChange?.(e, e.target.checked);
                },
            }}
            label={label}
            labelClassName={labelClassName}
            labelPosition={labelPosition}
            placeholder={placeholder}
            labelColumnWidth={labelColumnWidth}
            error={isFieldErrorVisible && error}
            warning={warning}
            info={info}
            alert={alert}
            description={description}
            required={required}
            disabled={disabled}
            addon={addon}
        />
    );
}

export type FormCheckboxField = typeof FormCheckboxField & FormCheckboxFieldStatics;

FormCheckboxField.LABEL_POSITION = FormField.LABEL_POSITION;
